import { useEffect, useState } from "react";
import Modal from "../../../components/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getAxiosInstance } from "../../../utils/axiosInstance";
import { Divider, List, ListItem, Tag, TagLabel, TagLeftIcon, Text, Tooltip, useColorModeValue } from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { getTransactionsTypeIcon } from "../../../globals/transactions";
import dayjs from "dayjs";
import { useUserContext } from "../../../utils/userContext";
import { Product } from "../../Products/product.type";
import {useParams} from "react-router-dom";
import {IoRepeatOutline} from "react-icons/io5";

interface InventoryModal {
    open: boolean;
    onClose: () => void;
    id: string | null;
    placeOfBusinessId?: string;
    name: string;
}

const InventoryModal = (props: InventoryModal) => {
    const { t } = useTranslation("translations");
    const { permission } = useUserContext();
    const { id } = useParams();

    const [data, setData] = useState<any | null>(null);
    const [loadingData, setLoadingData] = useState<boolean>(false);

    const [productData, setProductDate] = useState<Product | null>(null);
    const [loadingProduct, setLoadingProduct] = useState<boolean>(false);

    const [sellingPrice, setSellingPrice] = useState<any | null>(null);

    const fetchProduct = async (id: string) => {
        setLoadingProduct(true);
        try {
            const res = await getAxiosInstance().get("/Products/" + id);
            setProductDate(res.data);
        } catch (e) {
            toast.error(t("FETCH_DATA_ERROR"), { position: "bottom-right" });
        } finally {
            setLoadingProduct(false);
        }
    }

    const fetchSellingPrice = async (id: string) => {
        try {
            const req = await getAxiosInstance().get(`/ProductPriceHistory/${id}`);
            setSellingPrice(req.data);
        } catch (error) {
            // toast.error(t("FETCH_DATA_ERROR"), { position: toast.POSITION.BOTTOM_RIGHT })
            setSellingPrice(null);
        }
    }


    const fetchTransaction = async (productId: string, placeOfBusinessId: string) => {
        setLoadingData(true);
        try {
            const res = await getAxiosInstance().get("/Inventory/PlaceOfBusiness/Transactions", {
                params: {
                    placeOfBusinessId,
                    productId
                }
            });
            setData(res.data);
        } catch (e) {
            toast.error(t("FETCH_DATA_ERROR"), { position: toast.POSITION.BOTTOM_RIGHT })
        } finally {
            setLoadingData(false);
        }
    }

    useEffect(() => {
        if (props.id && props.placeOfBusinessId) {
            fetchProduct(props.id);
            fetchSellingPrice(props.id);
            fetchTransaction(props.id, props.placeOfBusinessId);
        }
    }, [props.id, props.placeOfBusinessId])

    return (
        <Modal
            isOpen={props.open}
            onClose={props.onClose}
            header={t("INVENTORY_ENTRY_MODAL")}
            secondaryActionBtnType="button"
            size="full"
        >
            <>
                {
                    productData && <div className="flex flex-col justify-start">
                        <Text
                            lineHeight={1.1}
                            fontWeight={600}
                            fontSize={{ base: '2xl', sm: '4xl', lg: '5xl' }}
                            className="hover:underline"
                        >
                            {
                                permission.findIndex((x) => x === "ViewProducts") !== -1 ?
                                <a href={"/product/" + props.id}>{productData.name} {props.name}</a> :
                                <>{productData.name} {props.name}</>
                            }
                        </Text>
                        <Text
                            color={'gray.400'}
                            fontSize={'md'}
                            fontWeight={'400'}
                            mb={'6'}
                            mt={"3"}
                        >
                            {productData.description ? productData.description : "-"}
                        </Text>
                        <Divider />
                        <List spacing={2} mb={'2'} mt={"2"}>
                            <ListItem>
                                <Text as={'span'} fontWeight={'bold'} className="mr-2">
                                    {t("Unit of Measurement")}:
                                </Text>
                                {productData.unitMeasurement.name}
                            </ListItem>
                            <ListItem>
                                <Text as={'span'} fontWeight={'bold'} className="mr-2">
                                    {t("SKU")}:
                                </Text>
                                {productData.sku}
                            </ListItem>
                            <ListItem mb={4}>
                                <Text as={'span'} fontWeight={'bold'}>
                                    {t("Categories")}:
                                </Text>
                            </ListItem>
                            <div className="flex flex-wrap">
                                {
                                    productData.categories && productData.categories.map((c: any, index: number) => {
                                        return (
                                            <Tag size='lg' colorScheme='blue' borderRadius='full' className="m-1">
                                                <TagLeftIcon as={CheckIcon} />
                                                <TagLabel> {c.name} </TagLabel>
                                            </Tag>
                                        )
                                    })
                                }
                            </div>
                            {
                                sellingPrice &&
                                <ListItem>
                                    <Text as={'span'} fontWeight={'bold'} className="mr-2">
                                        {t("SELLING_PRICE_RON")}:
                                    </Text>
                                    {sellingPrice.price.value}
                                </ListItem>
                            }
                            {
                                !sellingPrice &&
                                <ListItem>
                                    <Text as={'span'} fontWeight={'bold'} className="mr-2">
                                        {t("SELLING_PRICE_RON")}:
                                    </Text>
                                    {t("SELLING_PRICE_NOT_SET")}
                                </ListItem> 
                            }
                        </List>
                    </div>
                }
                <Divider className="my-2" />
                <Text fontSize="2xl" fontWeight="bold">
                    {t("INVENTORY_ACTIONS_HISTORY")}
                </Text>
                <Divider className="my-2" />
                {
                    data && data.map((row: any, index: number) =>
                        <div
                            key={`place_of_business_inventory_transfer_${index}`}
                            className="flex flex-row bg-teal-300/20 m-2 rounded-xl p-2 items-center justify-between text-[18px]"
                        >
                            <div className="py-2 px-4 bg-white/80 rounded-full mr-2 relative w-[15%]">
                                    {
                                        // For the transfers here we are going to make the in transfer blue and the out red
                                        row.type === 2 ?
                                          <div>
                                                <div
                                                  className="flex flex-row justify-center items-center start relative">
                                                    <Tooltip label={t("INVENTORY_TRANSFER")} placement='auto'>
                                                        <>
                                                            <IoRepeatOutline
                                                              color={row.transfer?.fromPlaceOfBusinessId == id ? "red" : "blue"}
                                                              width={20}
                                                              height={20}
                                                            />
                                                            <Text className="ml-2"> {t("INVENTORY_TRANSFER")}</Text>
                                                        </>
                                                    </Tooltip>
                                                </div>
                                          </div> :
                                          getTransactionsTypeIcon(row.type, t)
                                    }
                            </div>
                            <div className="flex flex-row w-[20%]">
                                {t("QUANTITY")}: {row.quantity}
                            </div>
                            <div className="w-[15%]">
                                {t("PRICE_RON")}: {row.price}
                            </div>
                            <div className="w-[15%]">
                                {row.user.displayName}
                            </div>
                            <div className="mr-2">
                                {dayjs(row.createdAt).format("DD-MM-YYYY HH:mm")}
                            </div>
                        </div>
                    )
                }
            </>
        </Modal>
    )
}

export default InventoryModal;